/* ═══════════════════════════════════════════════════════════════════════════
   TELAR — el telar como mundo 3D, recorrido con el scroll.

   No es un fondo decorativo: la escena ES un telar. La urdimbre son hilos
   tensados en el eje Y, la trama son hilos que cruzan en X, y el scroll es la
   lanzadera: según bajas, la trama avanza y la tela se teje delante de ti.
   Al final del recorrido hay tela donde al principio solo había hilos sueltos.

   Cuatro de los hilos llevan los colores de Google. No es adorno: son los
   cuatro canales que mide el sitio, y cada capítulo enciende el suyo.

   Todo el dibujo son líneas (LineSegments): sin materiales caros, sin luces,
   sin sombras. Una escena de líneas se mueve a 60 fps en un teléfono, que es
   donde esto tiene que aguantar.

   Este archivo es solo el entrante: instala el observador de capítulos y, si
   procede, pide la escena. Three.js no se menciona aquí a propósito — vive
   detrás de un `import()` en escena.js.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Los capítulos se revelan con el scroll: eso es contenido, no decoración, y
   no puede esperar a que baje Three.js. Se instala aquí, en el entrante, que
   pesa menos de 1 kB y corre en cuanto termina de analizarse el documento. */
const caps = [...document.querySelectorAll('.cap')];

/* El estado oculto de los capítulos lo pone este módulo, no el CSS, y en la
   misma tarea marca los que ya están en pantalla. Las dos cosas juntas, o no
   sirve de nada:

   Chrome descarta para siempre como candidato a LCP cualquier elemento cuyo
   primer pintado sea `opacity:0`. Con el oculto escrito en la hoja, el
   capítulo visible nacía apagado —el observador es asíncrono y llega un
   cuadro tarde— y quedaba fuera de la métrica. Lo que Lighthouse acababa
   midiendo era el lienzo del telar, que es decoración; y al dejar de tejerse
   durante la carga, en un enlace con ancla no quedaba candidato ninguno:
   «NO_LCP», nota 0, ocho de cada diez pasadas.

   Haciéndolo aquí, entre `add('animar')` y `add('vis')` no cabe un pintado:
   el capítulo que el visitante está mirando nunca llega a estar apagado. Y de
   propina, si este módulo no corre, la página se ve entera en vez de en
   blanco. */
const encendidos = new Set();
for (const c of caps) {
  const r = c.getBoundingClientRect();
  if (r.top < innerHeight && r.bottom > 0) encendidos.add(c);
}

/* Y el capítulo al que apunta el ancla, aunque todavía no esté en pantalla:
   el salto ocurre DESPUÉS de que corra este módulo —medido: el módulo a los
   186 ms, el salto a los 233—, así que mirar el rectángulo no basta. Quien
   abre `/#diagnostico` va a caer ahí dentro; no hay entrada que animar. */
try {
  const destino = location.hash.length > 1 && document.querySelector(location.hash);
  const cap = destino && destino.closest('.cap');
  if (cap) encendidos.add(cap);
} catch { /* un ancla que no es un selector válido no es un error, es un ancla que no existe */ }

/* El orden importa, y cuesta un rato descubrirlo: `animar` se pone DESPUÉS de
   medir. Al revés, el `getBoundingClientRect()` del bucle obliga al navegador
   a resolver el estilo con los capítulos ya apagados, y eso arranca la
   transición de opacidad desde cero; el `.vis` de la línea siguiente la
   invierte, pero el primer pintado llega con el capítulo casi transparente y
   Chrome lo descarta igual. Midiendo primero no hay un solo recálculo entre
   el apagado y el encendido. */
document.documentElement.classList.add('animar');
encendidos.forEach((c) => c.classList.add('vis'));

/* ── El primer gesto ──────────────────────────────────────────────────────
   Un solo juego de escuchas para todo lo que no debe ocurrir hasta que haya
   alguien de verdad al otro lado.

   `scroll` no está en la lista a propósito: abrir `/#diagnostico` salta al
   ancla y eso también dispara `scroll`, sin que nadie haya tocado nada. Los
   cuatro de abajo sí son gestos de una persona, y no hay forma humana de
   recorrer la página sin pasar por alguno. */
const gestos = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
const opciones = { passive: true, once: true };
const pendientes = [];
const alPrimerGesto = (f) => pendientes.push(f);
const soltar = () => {
  gestos.forEach((g) => removeEventListener(g, soltar, opciones));
  while (pendientes.length) pendientes.shift()();
};
gestos.forEach((g) => addEventListener(g, soltar, opciones));

/* El desplazamiento suave, el primero de la lista. El salto inicial a un ancla
   tiene que ser instantáneo —el porqué está en `html.suave` de la hoja— y no
   basta con esperar a `load`: Chrome reintenta el salto al terminar de cargar,
   y si para entonces ya está en `smooth` vuelve la animación de 40 000 px que
   invalida el LCP. Lo suave es para los enlaces que alguien pulsa, y pulsar es
   un gesto. */
alPrimerGesto(() => document.documentElement.classList.add('suave'));

/* ── El encendido es de ida y no de vuelta ─────────────────────────────────
   Esto era `toggle('vis', e.isIntersecting)` con `threshold: 0.25`, y en un
   teléfono resultó ser un fallo grave: el umbral exige que el 25 % de la
   SECCIÓN esté en pantalla, y en móvil las secciones son mucho más altas que
   la pantalla. `#negocios` mide 2.890 px contra 844 de viewport: su
   proporción máxima posible es 0,29 — pasa raspando. Al abrir la ficha de un
   oficio la sección crece 1.400 px más, la proporción cae a 0,195, el
   observador la da por no intersecada y le quita `.vis`.

   Y `.vis` es lo que enciende `.in`. O sea: pulsabas un oficio y la sección
   entera se iba a `opacity:0`. Medido: ratio 0.195 · vis false · opacidad 0.
   En una pantalla de 568 px ni el hero llegaba al 25 %.

   Un aparecido no necesita desaparecer. Se enciende en cuanto asoma —umbral
   cero, con un margen inferior para que el fundido no empiece pegado al
   borde— y se deja de observar. Nada puede volver a apagarlo. */
const obs = new IntersectionObserver(
  (ent) => ent.forEach((e) => {
    if (!e.isIntersecting) return;
    e.target.classList.add('vis');
    obs.unobserve(e.target);
  }),
  { rootMargin: '0px 0px -10% 0px' }
);
caps.forEach((c) => { if (!encendidos.has(c)) obs.observe(c); });

/* ── El telar, cuando alguien lo pida ─────────────────────────────────────
   Tres puertas antes de bajar 133 kB de biblioteca:
     · que exista el lienzo,
     · que el visitante no haya pedido ahorrar datos,
     · que el navegador tenga WebGL.
   Y aun pasándolas, se espera al primer gesto, a que las fuentes hayan
   intercambiado y a que el hilo principal esté ocioso de verdad. El telar es
   el fondo: nunca puede retrasar el titular que lleva encima. */
const lienzo = document.getElementById('mundo');

/* La pregunta cuesta un contexto WebGL de verdad —unos 20 ms en un teléfono,
   medidos— y además lo deja vivo: Chrome solo tolera un puñado a la vez. Se
   devuelve en cuanto responde, y no se pregunta hasta que hace falta. */
function hayWebGL() {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') || c.getContext('webgl');
    if (!gl) return false;
    const soltar = gl.getExtension('WEBGL_lose_context');
    if (soltar) soltar.loseContext();
    return true;
  } catch { return false; }
}

const ahorro = !!(navigator.connection && navigator.connection.saveData);

if (lienzo && !ahorro) {
  /* Sin `timeout`. Con él, `requestIdleCallback` deja de esperar a los 3 s y
     entra a la fuerza — y en un teléfono lento esos 3 s caen justo dentro de
     la ventana que Chrome mide como bloqueo del hilo principal. Sin timeout
     solo entra cuando el hilo está ocioso de verdad, que es la condición que
     nos importa. */
  const pedir = () => {
    if (!hayWebGL()) return;
    const ocioso = window.requestIdleCallback || ((f) => setTimeout(f, 300));
    ocioso(() => { import('./escena.js').then((m) => m.tejer(lienzo)).catch(() => {}); });
  };

  /* ── El telar no se teje hasta que alguien mueve la lanzadera ───────────
     Antes bajaba en cuanto el hilo quedaba ocioso tras `load`. Era lo
     razonable y aun así salía caro: crear el contexto WebGL, compilar los
     sombreadores y pintar el primer cuadro son ~120 ms seguidos en un
     teléfono, y caían dentro de la ventana que Chrome mide como bloqueo.
     Medido cinco veces sobre la misma página, la nota de rendimiento saltaba
     entre 99 y 79 según si esa tanda caía dentro de la ventana o justo
     después: una moneda al aire, no una medida.

     Ahora se espera al primer gesto. No es un truco para la auditoría: es lo
     que la escena ya decía de sí misma — el scroll ES la lanzadera, y con la
     página quieta arriba no hay una sola pasada que tejer. Quien entra, lee
     el titular y se va, no paga los 133 kB de la biblioteca ni un cuadro de
     GPU. Quien baja, teje.

     A cambio, el fondo está liso hasta el primer desplazamiento. Detrás del
     velo del 72 % eso es casi todo lo que se veía. */
  alPrimerGesto(() => {
    /* Y aún se espera una cosa más: a que las fuentes hayan intercambiado.
       El elemento que Chrome mide como LCP es texto —el párrafo del hero—, y
       con `font-display:swap` ese texto se pinta primero con la letra del
       sistema y se REPINTA al llegar el woff2. Ese repintado es el LCP de
       verdad. Bajar la biblioteca antes le quitaría ancho de banda a algo
       que el visitante está esperando ver. */
    const f = document.fonts && document.fonts.ready;
    if (f && typeof f.then === 'function') f.then(pedir, pedir);
    else pedir();
  });
}
